// C-Tribe Startup Alpha 2027 application (/form/tech/startup-alpha). Controls
// render one question per screen in key order; schemas.ts builds the base
// schema from these controls and applies startupAlphaSchemaOverrides on top.
import { z, ZodRawShape } from "zod";
import { FormControl, TextLengthCheckOptionType } from "@/models/form-types";
import { defaultRadioOptions, ERROR_MESSAGES, industryOptions } from "@/helpers/utils/form-constants";
import {
  createAcceptControl, createCheckboxControl, createEmailControl, createFileControl, createIntroControl,
  createPhoneControl, createRadioControl, createTextControl, createTextareaControl, createUrlControl,
} from "@/helpers/utils/utility-functions";
import {
  STARTUP_ALPHA_CLOSES_ON, STARTUP_ALPHA_INELIGIBLE_RAISE, STARTUP_ALPHA_MAX_GOALS, STARTUP_ALPHA_MAX_YEAR,
  STARTUP_ALPHA_MIN_YEAR, STARTUP_ALPHA_RAISED_ERROR, STARTUP_ALPHA_YEAR_ERROR,
  startupAlphaAmountRaisingOptions, startupAlphaEmergingTechOptions, startupAlphaExpoDayOptions,
  startupAlphaGoalOptions, startupAlphaRaisedOptions, startupAlphaStageOptions,
} from "@/helpers/utils/startup-alpha-constants";

export const STARTUP_ALPHA_VALUE_PROP_MAX_WORDS = 200;
export const STARTUP_ALPHA_TRACTION_MAX_WORDS = 150;

const LINK = 'class="text-blue-500" target="_blank" rel="noopener noreferrer"';
const startupAlphaConsent =
  `By hitting 'I accept', I confirm the information above is accurate and that I can apply on behalf of this company, and I agree that C-Tribe representatives may contact me by email or phone about this application, C-Tribe Startup Alpha and the festival. I have authority to enter into the <a href="https://www.ctribefestival.com/terms/" ${LINK}>Terms of Service</a>, and have read the <a href="https://www.ctribefestival.com/privacy/" ${LINK}>Privacy Policy</a>.`;

export const startupAlphaControls: Record<string, FormControl> = {
  intro: createIntroControl("C-Tribe Startup Alpha 2027", [
    "Accepted startups attend C-Tribe Festival for free, with 2 Tech Passes and one exhibit day on Village Expo Builders' Row (Thursday, August 12 or Friday, August 13, 2027).",
    "Startup Alpha is for startups founded in 2022 or later that have raised under $1M CAD, with a live product or MVP and a website.",
    `We review applications on a rolling basis. Applications close ${STARTUP_ALPHA_CLOSES_ON}.`,
  ]),
  firstName: createTextControl("What is your first name?"),
  lastName: createTextControl("What is your last name?"),
  basedIn: { ...createTextControl("City"), example: "Edmonton, AB" },
  email: createEmailControl("What email address can we reach you at?"),
  phone: createPhoneControl("What's the best phone number to reach you?"),
  companyName: createTextControl("Company name"),
  websiteURL: createUrlControl("Website URL"),
  yearFounded: { ...createTextControl("Year your company was founded"), example: "2024" },
  totalRaised: createRadioControl("How much has your company raised to date (CAD)?", startupAlphaRaisedOptions),
  companyStage: createRadioControl("Company stage", startupAlphaStageOptions),
  valueProposition: createTextareaControl(
    "Describe your company's value proposition",
    `( ${STARTUP_ALPHA_VALUE_PROP_MAX_WORDS} words max )`,
    false, 1, STARTUP_ALPHA_VALUE_PROP_MAX_WORDS, TextLengthCheckOptionType.WORD_COUNT
  ),
  industry: createRadioControl("What is your industry?", industryOptions),
  emergingTech: createCheckboxControl(
    "Are you building with any emerging technologies?",
    startupAlphaEmergingTechOptions,
    "( optional ) Select all that apply.",
    true
  ),
  raisingCapital: createRadioControl("Are you raising capital now?", defaultRadioOptions),
  // Shown only when raisingCapital is "Yes" (form-branching.ts).
  amountRaising: createRadioControl("How much are you planning to raise (CAD)?", startupAlphaAmountRaisingOptions),
  traction: createTextareaControl(
    "Describe your traction so far",
    `Users, revenue, pilots, partnerships or anything that shows momentum. ( ${STARTUP_ALPHA_TRACTION_MAX_WORDS} words max )`,
    false, 1, STARTUP_ALPHA_TRACTION_MAX_WORDS, TextLengthCheckOptionType.WORD_COUNT
  ),
  goals: createCheckboxControl(
    "What are your top three goals for C-Tribe?",
    startupAlphaGoalOptions,
    "Select up to three."
  ),
  expoDay: createRadioControl("Which exhibit day works best?", startupAlphaExpoDayOptions),
  // Same key, type and limits as the tech/elevate pitch deck upload.
  pitchDeckUpload: createFileControl("Upload your pitch deck", "( optional )", 5, ["pdf"], true),
  acceptTerms: createAcceptControl(startupAlphaConsent),
};

const countWords = (text: string) => (text.trim() ? text.trim().split(/\s+/).length : 0);
const wordLimited = (max: number) =>
  z.string()
    .refine((value) => !!value?.trim(), { message: ERROR_MESSAGES.FieldRequired() })
    .refine((value) => countWords(value ?? "") <= max, { message: ERROR_MESSAGES.MaxWords(max) });

// Eligibility gates: an ineligible founding year or raise cannot be submitted.
export const startupAlphaSchemaOverrides: ZodRawShape = {
  yearFounded: z
    .string({ required_error: ERROR_MESSAGES.FieldRequired() })
    .trim()
    .regex(/^\d{4}$/, { message: "Enter the 4-digit year, for example 2024." })
    .refine((value) => Number(value) >= STARTUP_ALPHA_MIN_YEAR, { message: STARTUP_ALPHA_YEAR_ERROR })
    .refine((value) => Number(value) <= STARTUP_ALPHA_MAX_YEAR, { message: "Enter a year no later than 2027." }),
  totalRaised: z
    .enum(startupAlphaRaisedOptions, { required_error: ERROR_MESSAGES.SelectOne() })
    .refine((value) => value !== STARTUP_ALPHA_INELIGIBLE_RAISE, { message: STARTUP_ALPHA_RAISED_ERROR }),
  valueProposition: wordLimited(STARTUP_ALPHA_VALUE_PROP_MAX_WORDS),
  traction: wordLimited(STARTUP_ALPHA_TRACTION_MAX_WORDS),
  goals: z
    .array(z.enum(startupAlphaGoalOptions))
    .min(1, { message: ERROR_MESSAGES.SelectOne() })
    .max(STARTUP_ALPHA_MAX_GOALS, { message: ERROR_MESSAGES.MaxAllowed(STARTUP_ALPHA_MAX_GOALS) }),
};
